/* ==============================================================================
   Casca das telas de autenticação
   ==============================================================================
   Direção visual definida por referência do usuário: card CLARO sobre fundo em
   gradiente vivo, campos com apenas linha inferior e ícone à esquerda, botão em
   pílula com gradiente, e rodapé com o link alternativo em caixa alta.

   Duas notas sobre como isso convive com o resto do produto:

   1. **O card é claro, o app é escuro.** Não é inconsistência: a autenticação é
      uma tela de passagem, com fundo próprio e nenhuma relação com a interface
      de conversa. O `data-theme="light"` fica ESCOPADO nesta árvore, então os
      tokens se invertem aqui e em nenhum outro lugar — nada de valor de cor
      hardcoded.

   2. **Campos sem caixa exigem mais cuidado de acessibilidade, não menos.** Sem
      borda ao redor, o único indicador de foco é a linha inferior; ela precisa
      mudar de cor E de espessura, senão o estado de foco fica invisível para
      quem navega por teclado.
   ============================================================================== */

import { useState, type ReactNode } from 'react';
import { Logo } from '../assets/brand/Logo.tsx';

export interface AuthShellProps {
  /** Título grande no topo do card. */
  title: string;
  children: ReactNode;
  /** Faixa inferior do card. */
  footer?: ReactNode;
  instanceName?: string;
}

export function AuthShell({
  title,
  children,
  footer,
  instanceName = 'Concord',
}: AuthShellProps) {
  return (
    <div
      /* data-theme escopado: os tokens de cor invertem para o modo claro apenas
         dentro desta árvore. O app de conversa continua escuro. */
      data-theme="light"
      /* Padding vertical maior que o horizontal, e generoso de propósito: a
         assinatura da instância é posicionada no rodapé absoluto desta caixa, e
         com `p-5` o card encostava nela em janela baixa. */
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-8 sm:px-5 sm:py-12"
      style={{ background: 'var(--gradient-brand-backdrop)' }}
    >
      <style>{`
        @keyframes auth-card-enter {
          from { opacity: 0; transform: translateY(14px) scale(0.985) }
          to   { opacity: 1; transform: none }
        }
        @keyframes auth-spin { to { transform: rotate(360deg) } }

        /* Altura animada sem salto e sem espaço reservado.
           0fr -> 1fr é interpolável; height:auto não é. */
        .auth-collapse {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows var(--duration-normal) var(--ease-out-expo),
                      opacity var(--duration-fast) linear;
          opacity: 0;
        }
        .auth-collapse[data-open='true'] { grid-template-rows: 1fr; opacity: 1; }
        .auth-collapse > * { overflow: hidden; min-height: 0; }

        @media (prefers-reduced-motion: reduce) {
          @keyframes auth-card-enter { from { opacity: 0 } to { opacity: 1 } }
          .auth-collapse { transition: none }
        }
      `}</style>

      {/* Textura e brilho do fundo. aria-hidden: decoração pura. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{ background: 'var(--gradient-brand-sheen)' }}
      />

      <main
        className="relative z-10 flex w-full flex-col"
        style={{
          maxWidth: 'var(--layout-auth-card)',
          background: 'var(--color-surface-raised)',
          borderRadius: 'var(--radius-xl)',
          /* Sombra em duas camadas: uma curta define a silhueta contra o
             gradiente, uma longa e difusa dá a distância. */
          boxShadow: 'var(--shadow-float)',
          animation: 'auth-card-enter var(--duration-slow) var(--ease-spring) both',
        }}
      >
        {/* Corpo do card.
            O recuo lateral (px-8) é o que mantém a linha inferior dos campos
            DENTRO da silhueta do card. Sem ele, a linha ia de borda a borda e o
            primeiro campo — que fica na altura em que o canto arredondado ainda
            está curvando — parecia mais largo que os de baixo, onde a borda já é
            reta. A largura era a mesma; o que faltava era o recuo.

            pt maior que pb porque o topo carrega o logo: símbolo encostado na
            borda lê como erro de renderização, texto não. */}
        <div className="px-8 pb-6 pt-10">
          <header className="mb-6 flex flex-col items-center gap-3">
            <Logo size={34} variant="color" title="" />
            <h1
              className="font-bold"
              style={{
                /* --text-xl (22px) e não --text-2xl (28px): 28px é o tamanho de
                   título de TELA, e aqui a tela é um card de 372px. O título
                   ocupava quase um terço da largura útil e competia com o
                   formulário, que é o conteúdo. */
                fontSize: 'var(--text-xl)',
                letterSpacing: 'var(--tracking-tight)',
                lineHeight: 'var(--leading-tight)',
                color: 'var(--color-text-primary)',
              }}
            >
              {title}
            </h1>
          </header>

          {children}
        </div>

        {/* Faixa do rodapé, e por que ela tem padding próprio.
            A ação alternativa é um DESTINO diferente do formulário, então
            precisa de respiro dos dois lados: acima, para não parecer o segundo
            botão do formulário; abaixo, para não encostar na borda do card. */}
        {footer && <div className="px-8 pb-8 pt-2">{footer}</div>}
      </main>

      <footer
        className="absolute inset-x-0 bottom-3 z-10 text-center"
        style={{
          fontSize: 'var(--text-2xs)',
          /* Branco sólido, não 60% de branco: este texto fica SOBRE o gradiente,
             e a 60% media 1.59:1 no trecho mais claro da rampa. Sólido, na faixa
             onde o rodapé realmente cai, fica em 5.19:1 ou mais. A sombra é
             reforço de borda do glifo, não substituto de contraste. */
          color: 'var(--color-on-solid)',
          textShadow: 'var(--shadow-text)',
        }}
      >
        {instanceName} · by <span className="font-semibold">Jortega</span>
      </footer>
    </div>
  );
}

/* ==============================================================================
   Campo com linha inferior
   ==============================================================================
   Sem caixa: só o ícone à esquerda, o texto, e uma linha embaixo.

   O foco muda a COR e a ESPESSURA da linha. Só a cor seria insuficiente — numa
   linha de 1px a diferença de matiz é pequena demais para quem navega por
   teclado perceber, e é o único indicador de foco que sobra sem borda ao redor.
   ============================================================================== */

export interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'numeric' | 'text' | 'email';
  disabled?: boolean;
  required?: boolean;
  /** Ícone à esquerda. Um dos exportados abaixo. */
  icon?: ReactNode;
  /** Botão à direita, como o de mostrar senha. */
  trailing?: ReactNode;
  /** Monoespaçado e espaçado, para código de verificação. */
  mono?: boolean;
  /** Texto de apoio sob a linha. `| undefined` porque o chamador usa `??`. */
  hint?: string | undefined;
  invalid?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

export function Field({
  id, label, type, value, onChange, placeholder, autoComplete, inputMode,
  disabled, required, icon, trailing, mono, hint, invalid, ref,
}: FieldProps) {
  const [focused, setFocused] = useState(false);

  const lineColor = invalid
    ? 'var(--color-danger)'
    : focused
      ? 'var(--color-accent)'
      : 'var(--color-border-strong)';

  return (
    <div>
      <label
        htmlFor={id}
        /* 6px entre rótulo e campo, contra os 20px do FieldGroup entre campos:
           a proporção é o que agrupa. Perto = mesma coisa, longe = coisa
           seguinte. Com 4px o rótulo encostava no texto digitado. */
        className="mb-1.5 block font-medium"
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {label}
      </label>

      <div
        className="flex items-center gap-2"
        style={{
          paddingBottom: 'var(--spacing-2)',
          /* Espessura muda no foco. Só a cor não bastaria em 1px. */
          borderBottom: `${focused || invalid ? 2 : 1}px solid ${lineColor}`,
          /* Compensa a mudança de espessura para o campo não pular 1px. */
          marginBottom: focused || invalid ? 0 : 'var(--spacing-px)',
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color var(--duration-fast) var(--ease-out-quart)',
        }}
      >
        {icon && (
          <span
            aria-hidden="true"
            className="flex shrink-0 items-center"
            style={{
              color: focused ? 'var(--color-accent)' : 'var(--color-text-tertiary)',
              transition: 'color var(--duration-fast) var(--ease-out-quart)',
            }}
          >
            {icon}
          </span>
        )}

        <input
          ref={ref}
          id={id}
          name={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          disabled={disabled}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="min-w-0 flex-1 bg-transparent outline-none"
          style={{
            paddingTop: 'var(--spacing-1)',
            color: 'var(--color-text-primary)',
            fontSize: mono ? 'var(--text-lg)' : 'var(--text-base)',
            fontFamily: mono ? 'var(--font-mono)' : 'inherit',
            letterSpacing: mono ? 'var(--tracking-otp)' : 'var(--tracking-normal)',
            textAlign: mono ? 'center' : 'left',
          }}
        />

        {trailing}
      </div>

      {hint && (
        <p
          id={`${id}-hint`}
          className="mt-1.5"
          style={{
            fontSize: 'var(--text-2xs)',
            lineHeight: 'var(--leading-normal)',
            color: invalid ? 'var(--color-danger)' : 'var(--color-text-tertiary)',
          }}
        >
          {hint}
        </p>
      )}
    </div>
  );
}

/** Espaçamento entre campos. Separa o ritmo entre grupos do ritmo interno. */
export function FieldGroup({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-5">{children}</div>;
}

/* ==============================================================================
   Botão em pílula
   ============================================================================== */

export function SubmitButton({
  children,
  loading = false,
  disabled = false,
}: {
  children: ReactNode;
  loading?: boolean;
  disabled?: boolean;
}) {
  const blocked = loading || disabled;
  return (
    <button
      type="submit"
      disabled={blocked}
      /* mt-8 e não mt-6: este botão fecha o formulário, e antes dele vem o
         último campo com sua linha inferior. Com 24px a linha do campo e a
         pílula ficavam perto o bastante para ler como um par. */
      className="mt-8 flex w-full items-center justify-center gap-2 font-semibold transition-all active:scale-[0.99] disabled:cursor-not-allowed"
      style={{
        height: 'var(--spacing-12)',
        /* Pílula: raio maior que a metade da altura. */
        borderRadius: 'var(--radius-full)',
        background: blocked
          ? 'var(--color-surface-active)'
          : 'var(--gradient-brand-action)',
        /* Bloqueado usa text-secondary, não text-tertiary: sobre
           surface-active do tema claro o terciário media 4.12:1 e reprovava
           AA. E "Entrando" não é rótulo inerte — é o estado da operação em
           curso, informação que precisa ser lida. */
        color: blocked ? 'var(--color-text-secondary)' : 'var(--color-on-solid)',
        boxShadow: blocked ? 'none' : 'var(--shadow-action)',
        /* Caixa alta com espaçamento: sem o tracking, texto em caixa alta fica
           apertado e ilegível. */
        fontSize: 'var(--text-xs)',
        letterSpacing: 'var(--tracking-wider)',
        textTransform: 'uppercase',
        transitionDuration: 'var(--duration-fast)',
        transitionTimingFunction: 'var(--ease-out-quart)',
      }}
      onMouseEnter={(e) => {
        if (blocked) return;
        e.currentTarget.style.boxShadow = 'var(--shadow-action-hover)';
        e.currentTarget.style.filter = 'brightness(1.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = blocked ? 'none' : 'var(--shadow-action)';
        e.currentTarget.style.filter = 'none';
      }}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true"
         style={{ animation: 'auth-spin 0.7s linear infinite' }}>
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="2"
              opacity="0.3" fill="none" />
      <path d="M14.2 8a6.2 6.2 0 00-6.2-6.2" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* ==============================================================================
   Erro
   ==============================================================================
   NÃO reserva espaço quando vazio. `grid-template-rows: 0fr -> 1fr` dá altura
   zero sem erro e transição suave ao aparecer; reservar altura fixa deixaria um
   vão morto no formulário, que lê como bug de layout.

   `aria-live` fica no container, que existe SEMPRE: se o elemento só aparecesse
   junto com a mensagem, o leitor de tela não anunciaria a primeira.
   ============================================================================== */
export function ErrorSlot({ message }: { message?: string | null | undefined }) {
  return (
    <div
      className="auth-collapse"
      data-open={message ? 'true' : 'false'}
      role="alert"
      aria-live="polite"
    >
      <div>
        <div
          className="mt-4 flex items-start gap-2 px-3 py-2"
          style={{
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-danger-subtle)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-danger)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" className="mt-px shrink-0"
               aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="currentColor" />
            <path d="M8 4.5v4.2M8 11.2v.6" stroke="var(--color-surface-raised)"
                  strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span>{message}</span>
        </div>
      </div>
    </div>
  );
}

/* ==============================================================================
   Auxiliares
   ============================================================================== */

/** Link discreto alinhado à direita, como "Esqueci minha senha". */
export function InlineLink({
  label,
  onClick,
  align = 'right',
}: {
  label: string;
  onClick: () => void;
  align?: 'left' | 'right';
}) {
  return (
    <div className={`mt-3 flex ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
      <button
        type="button"
        onClick={onClick}
        className="transition-colors hover:underline"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
      >
        {label}
      </button>
    </div>
  );
}

/** Rótulo centralizado entre o botão e as opções externas. */
export function CenteredLabel({ children }: { children: ReactNode }) {
  return (
    <p
      className="mt-6 text-center"
      style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
    >
      {children}
    </p>
  );
}

/**
 * Botões circulares de login externo.
 *
 * Só renderiza o que estiver realmente configurado. Mostrar um ícone que leva a
 * 404 porque o provider não tem credencial é pior que não mostrar — o usuário
 * clica, falha, e conclui que o sistema está quebrado.
 */
export function SocialRow({
  providers,
  onSelect,
  disabled = false,
}: {
  providers: ReadonlyArray<{ id: string; label: string; color: string; icon: ReactNode }>;
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  if (providers.length === 0) return null;
  return (
    <div className="mt-3 flex items-center justify-center gap-3">
      {providers.map((provider) => (
        <button
          key={provider.id}
          type="button"
          onClick={() => onSelect(provider.id)}
          disabled={disabled}
          aria-label={`Entrar com ${provider.label}`}
          title={`Entrar com ${provider.label}`}
          className="grid place-items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
          style={{
            width: 'var(--spacing-10)',
            height: 'var(--spacing-10)',
            borderRadius: 'var(--radius-full)',
            /* provider.color é cor de MARCA de terceiro, vinda por prop. Não é
               token do produto e não deve ser: forçá-la para a paleta faria o
               botão do Google deixar de parecer o do Google. */
            background: provider.color,
            color: 'var(--color-on-solid)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {provider.icon}
        </button>
      ))}
    </div>
  );
}

/** Rodapé do card: pergunta discreta + ação em caixa alta. */
export function FooterPrompt({
  text,
  actionLabel,
  onAction,
}: {
  text: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
        {text}
      </span>
      <button
        type="button"
        onClick={onAction}
        className="font-bold transition-opacity hover:opacity-70"
        style={{
          fontSize: 'var(--text-xs)',
          letterSpacing: 'var(--tracking-wide)',
          textTransform: 'uppercase',
          color: 'var(--color-text-primary)',
        }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

/** Nota de rodapé dentro do card, para texto legal. */
export function FinePrint({ children }: { children: ReactNode }) {
  return (
    <p
      className="mt-5 text-center"
      style={{
        fontSize: 'var(--text-2xs)',
        color: 'var(--color-text-tertiary)',
        lineHeight: 'var(--leading-normal)',
      }}
    >
      {children}
    </p>
  );
}

/* ==============================================================================
   Ícones dos campos
   ==============================================================================
   Monolinha, 16px, `currentColor`. Herdam a cor do estado de foco do campo.
   ============================================================================== */

export function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="5.2" r="2.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2.9 13.6c0-2.6 2.3-4.2 5.1-4.2s5.1 1.6 5.1 4.2"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.6"
            stroke="currentColor" strokeWidth="1.3" />
      <path d="M2.4 4.6L8 8.8l5.6-4.2" stroke="currentColor" strokeWidth="1.3"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3.2" y="7" width="9.6" height="6.6" rx="1.6"
            stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.6 7V5.2a2.4 2.4 0 014.8 0V7" stroke="currentColor"
            strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function AtIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10.5 8v1.1a1.8 1.8 0 003.1 1.2A6.2 6.2 0 108 14.2"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="8" r="1.9" stroke="currentColor" strokeWidth="1.3" />
      {crossed && (
        <path d="M2.5 13.5L13.5 2.5" stroke="currentColor" strokeWidth="1.3"
              strokeLinecap="round" />
      )}
    </svg>
  );
}

/** `tabIndex={-1}` mantém o Tab indo do campo direto para o próximo campo. */
export function PasswordToggle({
  shown,
  onToggle,
}: {
  shown: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      /* rounded-sm e não rounded: `rounded` resolve pelo --radius padrão do
         Tailwind, que não é token deste design system. */
      className="grid size-6 shrink-0 place-items-center rounded-sm transition-opacity hover:opacity-70"
      style={{ color: 'var(--color-text-tertiary)' }}
      aria-label={shown ? 'Ocultar senha' : 'Mostrar senha'}
      aria-pressed={shown}
      tabIndex={-1}
    >
      <EyeIcon crossed={shown} />
    </button>
  );
}
